import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import CallEndIcon from "@mui/icons-material/CallEnd";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import SwapCallsIcon from "@mui/icons-material/SwapCalls";

import { colors, alphaVar } from "../../../ui/colors";
import { useContactCenterTheme } from "../../theming/ContactCenterThemeContext";
import { useOptionalInternalWebRTC } from "../../../internal-contacts/_context/InternalWebRTCContext";
import { useOptionalVirtualAssistant } from "../../context/VirtualAssistantContext";
import { InCall } from "../in-call/InCall";
import { InternalOutgoingCall } from "../outgoing-call/InternalOutgoingCall";
import { useOptionalCallArbitration } from "./CallArbitrationContext";

export const AlternatingCallsView = () => {
  const { theme } = useContactCenterTheme();
  const arbitration = useOptionalCallArbitration();
  const internal = useOptionalInternalWebRTC();
  const voip = useOptionalVirtualAssistant();

  if (!arbitration?.alternating || !internal || !voip) return null;

  const { heldSide, pstnActiveKey, webrtcLabel, pstnLabel, switchActive } = arbitration;

  const webrtcLive = heldSide === "pstn";
  const heldLabel = webrtcLive ? pstnLabel : webrtcLabel;

  const hangupHeld = () => {
    if (webrtcLive) {
      if (pstnActiveKey) voip.hangupCallByCompany(pstnActiveKey);
    } else {
      void internal.hangup();
    }
  };

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={switchActive}
        sx={{
          cursor: "pointer",
          px: 1.25,
          py: 0.75,
          mb: 1,
          borderRadius: 1.5,
          border: `1px solid ${theme.shell.border}`,
          backgroundColor: alphaVar(theme.shell.accent, 0.06),
          transition: "background-color 0.15s ease",
          "&:hover": { backgroundColor: alphaVar(theme.shell.accent, 0.12) },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <PauseCircleOutlineIcon sx={{ fontSize: "1.1rem", color: colors.warning.base }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: "0.82rem", fontWeight: 600, color: theme.shell.textPrimary }}>
              {heldLabel}
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: theme.shell.textSecondary }}>
              On hold · tap to switch
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Switch to this call">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                switchActive();
              }}
              sx={{ color: theme.shell.accent, "&:hover": { backgroundColor: alphaVar(theme.shell.accent, 0.14) } }}
            >
              <SwapCallsIcon sx={{ fontSize: "1.1rem" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="End call on hold">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                hangupHeld();
              }}
              sx={{ color: colors.danger.base, "&:hover": { backgroundColor: alphaVar(colors.danger.base, 0.1) } }}
            >
              <CallEndIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {webrtcLive && internal.internalCallView === "outgoing-ringing" ? (
          <InternalOutgoingCall />
        ) : (
          <InCall
            forceChannel={webrtcLive ? "webrtc" : "twilio"}
            deviceKey={pstnActiveKey ?? undefined}
          />
        )}
      </Box>
    </Box>
  );
};
